import React from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Avatar,
  Button,
} from "@mui/material";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import GroupIcon from "@mui/icons-material/Group";
import Colorapps from "../../../configcss/color";
import Navbar from "../../../Components/Navbar";
import Footer from "../../../Components/Footer";

const NST_LOGO = "path/to/nst-logo.png";
const LIHS_LOGO = "path/to/lihs-logo.png";

const foodProgramFeatures = [
  {
    icon: <LocalDiningIcon fontSize="large" />,
    title: "Nutritious Meals",
    description:
      "We deliver meals prepared with high-quality ingredients ensuring balanced nutrition.",
  },
  {
    icon: <GroupIcon fontSize="large" />,
    title: "Community Impact",
    description:
      "We partner with communities to reach the most vulnerable and create a positive impact.",
  },
  {
    icon: <LocalDiningIcon fontSize="large" />,
    title: "Hygienic Standards",
    description:
      "All meals are prepared under strict hygiene protocols to ensure safety and quality.",
  },
];

const FoodProgram = () => {
  return (
    <>
      <Navbar />
      <Box sx={{ backgroundColor: Colorapps.textprimary }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            height: { xs: "50vh", md: "70vh" },
            background: `url("https://img.freepik.com/free-photo/copy-space-italian-food-ingredients_23-2148551732.jpg") center/cover no-repeat`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.8))",
            }}
          />
          <Box
            sx={{
              position: "relative",
              color: Colorapps.textprimary,
              textAlign: "center",
              zIndex: 1,
              px: { xs: 2, md: 4 },
            }}
          >
            <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              sx={{
                fontFamily: "Poppins, sans-serif",
                textShadow: "0px 2px 10px rgba(0,0,0,0.8)",
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Abdullah bin Salam (رضی اللہ عنہ) Reported
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1rem", md: "1.3rem" },
                fontFamily: "Roboto, sans-serif",
                textShadow: "0px 2px 5px rgba(0,0,0,0.8)",
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              I heard the Messenger of Allah (ﷺ) saying, “O people, exchange greetings of peace (i.e., say: As-Salamu ‘Alaikum to one another), feed people, strengthen the ties of kinship, and be in prayer when others are asleep, you will enter Jannah in peace.”
              <br />
              <strong>[Al-Tirmidhi]</strong>
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 4,
                backgroundColor: Colorapps.textprimary,
                color: Colorapps.themesecondary,
                fontWeight: "bold",
                padding: "10px 30px",
                borderRadius: "25px",
                "&:hover": {
                  backgroundColor: Colorapps.themeaccent,
                  transform: "scale(1.05)",
                  transition: "all 0.3s ease",
                },
              }}
              href="#food-program"
            >
              Learn More
            </Button>
          </Box>
        </Box>

        {/* Food Program Section */}
        <Box
          sx={{
            py: 10,
            backgroundColor: Colorapps.backgroundlight,
            boxShadow: `0 4px 20px ${Colorapps.shadowcolor}`,
          }}
          id="food-program"
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                mb: 6,
                flexWrap: "wrap",
              }}
            >
              <Box
                component="img"
                src={NST_LOGO}
                alt="NST Food Programme Logo"
                sx={{ height: { xs: "60px", md: "80px" }, objectFit: "contain" }}
              />
              <Box
                component="img"
                src={LIHS_LOGO}
                alt="Lahore Institute of Health Sciences Logo"
                sx={{ height: { xs: "60px", md: "80px" }, objectFit: "contain" }}
              />
            </Box>

            <Box textAlign="center" mb={6}>
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  color: Colorapps.textsecondary,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Our Food Program
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  color: Colorapps.textsecondary,
                  fontStyle: "italic",
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                Providing sustainable and nutritious meals to communities in need.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {foodProgramFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={3}
                    sx={{
                      padding: 4,
                      textAlign: "center",
                      borderRadius: "15px",
                      backgroundColor: Colorapps.textprimary,
                      border: `1px solid ${Colorapps.themeaccent}`,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: `0px 10px 20px ${Colorapps.shadowcolor}`,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: Colorapps.themeaccent,
                        mb: 2,
                        color: Colorapps.textsecondary,
                        padding: 2,
                        width: 60,
                        height: 60,
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: "bold",
                        color: Colorapps.textsecondary,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        color: Colorapps.textsecondary,
                        fontSize: "1rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Collaboration Section */}
            <Box
              sx={{
                mt: 15,
                p: 4,
                backgroundColor: Colorapps.themeaccent,
                borderRadius: "15px",
                textAlign: "center",
                color: Colorapps.textsecondary,
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ fontFamily: "Poppins, sans-serif" }}
              >
                Our Collaborations
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "1.1rem",
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Naimat Saleem Trust, Ghani Foundation, Orient Group, and Sheikh Ejaz Ahmed Trust (Lake City Holdings) collaborated through the Lahore Institute of Health Sciences Trust to provide free quality meals.
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  borderColor: Colorapps.textsecondary,
                  color: Colorapps.textsecondary,
                  fontWeight: "bold",
                  padding: "10px 30px",
                  borderRadius: "25px",
                  "&:hover": {
                    backgroundColor: Colorapps.textsecondary,
                    color: Colorapps.themeaccent,
                    borderColor: Colorapps.textsecondary,
                  },
                }}
              >
                Read More
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default FoodProgram;
